import type {
  EntityEventRelationship,
  Event,
  EventTimeOfDay,
  EventVenue,
} from '../types/api';
import {
  compareEventLocalDates,
  formatEventLocalDateTime,
  formatEventLocalDuration,
  getEventLocalDurationHours,
  getEventLocalSortKey,
  hasKnownEventTime,
  parseEventLocalDateTime,
} from './eventDateTime';
import { getEventCoordinates, getLocationLabel, getVenueFormattedAddress, getVenueName } from './eventFields';
import { resolveEventTimeZone, type EventFormat } from './eventTimeZone';

/**
 * Single source of truth for how an event is described across every surface:
 * entity pages, search results, home cards, grid view, map popups, exports and
 * the detail page. Anything rendered as a badge or a metadata line is derived
 * here so the same event never reads differently in two places.
 */

export const RECENT_PAST_DAYS = 14;
export const RECENTLY_ADDED_DAYS = 7;

// ---------------------------------------------------------------------------
// Format (in-person / virtual / hybrid)
// ---------------------------------------------------------------------------

/**
 * The schema has no `isHybrid` column. An event flagged virtual that also has a
 * real physical coordinate is hybrid — that is exactly the population that
 * should keep a map marker while still advertising an online option.
 */
export function getEventFormat(event: Partial<Event> | null | undefined): EventFormat {
  if (!event) return 'in-person';
  const isVirtual = event.isVirtual === true || (event.isVirtual as unknown) === 1;
  if (!isVirtual) return 'in-person';
  return getEventCoordinates(event) ? 'hybrid' : 'virtual';
}

export const EVENT_FORMAT_LABELS: Record<EventFormat, string> = {
  'in-person': 'In-person',
  virtual: 'Virtual',
  hybrid: 'Hybrid',
};

/** Geographic results and map markers only include events with coordinates. */
export function isMappable(event: Partial<Event> | null | undefined): boolean {
  return getEventFormat(event) !== 'virtual' && getEventCoordinates(event) != null;
}

// ---------------------------------------------------------------------------
// Relationship
// ---------------------------------------------------------------------------

const RELATIONSHIP_LABELS: Record<string, string> = {
  HOSTING: 'Hosting',
  SPONSORING: 'Sponsor',
  SPONSOR: 'Sponsor',
  ATTENDING: 'Attending',
};

/** Map a stored relationship enum to its display label. '' when unknown. */
export function formatRelationship(relationship?: string | null): string {
  if (!relationship || typeof relationship !== 'string') return '';
  return RELATIONSHIP_LABELS[relationship.trim().toUpperCase()] ?? '';
}

export interface EventEntityRef {
  id?: number | null;
  entityId?: number | null;
  name?: string | null;
  entityName?: string | null;
  displayName?: string | null;
  type?: string | null;
  entityType?: string | null;
  role?: string | null;
  slug?: string | null;
  typeSlug?: string | null;
  canonicalUrl?: string | null;
}

export interface RelationshipBadge {
  key: string;
  /** "Hosting" on an entity page, "Elastic · Hosting" elsewhere. */
  label: string;
  relationship: string;
  entity: EventEntityRef | null;
  href: string | null;
}

/**
 * Relationship badges for an event.
 *
 * `contextEntityId` is the entity whose page we are on: its own badge collapses
 * to the bare relationship, and it is always listed first.
 */
export function getRelationshipBadges(
  event: Partial<Event> & { entities?: EventEntityRef[] },
  contextEntityId?: number | null,
): RelationshipBadge[] {
  const fallbackRelationship = formatRelationship(event.entityRelationship);

  const all: EventEntityRef[] = Array.isArray(event.entities) && event.entities.length > 0
    ? event.entities
    : event.entity
      ? [{ id: event.entity.id, name: event.entity.name, type: event.entity.type }]
      : [];

  if (all.length === 0) {
    return fallbackRelationship
      ? [{
          key: 'relationship',
          label: fallbackRelationship,
          relationship: fallbackRelationship,
          entity: null,
          href: null,
        }]
      : [];
  }

  const seen = new Set<number | string>();
  const unique = all.filter((entity) => {
    const key = entity.id ?? entity.entityId ?? entity.name ?? entity.entityName ?? '';
    if (!key || seen.has(key)) return false;
    seen.add(key);
    return true;
  });

  const ordered = contextEntityId != null
    ? [...unique].sort((a, b) =>
        Number((b.id ?? b.entityId) === contextEntityId) -
        Number((a.id ?? a.entityId) === contextEntityId))
    : unique;

  return ordered.flatMap((entity) => {
    const relationship = formatRelationship(entity.role) || fallbackRelationship;
    if (!relationship) return [];
    const id = entity.id ?? entity.entityId;
    const isContext = contextEntityId != null && id === contextEntityId;
    const name = entity.displayName || entity.name || entity.entityName || '';
    return {
      key: `relationship-${id ?? name}-${relationship}`,
      label: isContext || !name ? relationship : `${name} · ${relationship}`,
      relationship,
      entity,
      href: entity.canonicalUrl || null,
    };
  });
}

/** Confirmed University hosts. Legacy event-wide relationships never qualify. */
export function getUniversityHosts(
  event: { entities?: EventEntityRef[] } | null | undefined,
): EventEntityRef[] {
  const hosts = new Map<number | string, EventEntityRef>();
  for (const entity of event?.entities || []) {
    const type = String(entity.type ?? entity.entityType ?? '').trim().toLowerCase();
    const role = String(entity.role || '').trim().toUpperCase();
    if (type !== 'university' || role !== 'HOSTING') continue;
    const id = entity.id ?? entity.entityId;
    const name = entity.displayName || entity.name || entity.entityName || '';
    const key = id ?? name;
    if (key !== '') hosts.set(key, entity);
  }
  return Array.from(hosts.values());
}

// ---------------------------------------------------------------------------
// Time of day
// ---------------------------------------------------------------------------

const TIME_OF_DAY_LABELS: Record<EventTimeOfDay, string> = {
  MORNING: 'Morning',
  AFTERNOON: 'Afternoon',
  EVENING: 'Evening',
};

/**
 * Prefer the stored enrichment. Otherwise derive from the local start time:
 * morning 00:00–11:59, afternoon 12:00–17:59, evening 18:00–23:59.
 * All-day events and events with no reliable start time get no badge.
 */
export function getTimeOfDay(event: Partial<Event> | null | undefined): string {
  if (!event) return '';
  const stored = event.timeOfDay;
  if (stored && TIME_OF_DAY_LABELS[stored as EventTimeOfDay]) {
    return TIME_OF_DAY_LABELS[stored as EventTimeOfDay];
  }

  if (!hasKnownEventTime(event.startDate)) return '';
  const parsed = parseEventLocalDateTime(event.startDate);
  if (!parsed) return '';

  // Without a resolvable zone we are still reading the source's own wall clock,
  // which is the value the organiser published — safe to bucket.
  if (parsed.hour < 12) return TIME_OF_DAY_LABELS.MORNING;
  if (parsed.hour < 18) return TIME_OF_DAY_LABELS.AFTERNOON;
  return TIME_OF_DAY_LABELS.EVENING;
}

// ---------------------------------------------------------------------------
// Vibes
// ---------------------------------------------------------------------------

/** 'HANDS-ON' -> 'Hands-on', 'ROUNDTABLE' -> 'Roundtable'. */
export function humanizeVibe(vibe?: string | null): string {
  if (!vibe || typeof vibe !== 'string') return '';
  const trimmed = vibe.trim();
  if (!trimmed) return '';
  return trimmed
    .toLowerCase()
    .replace(/^[a-z]/, (c) => c.toUpperCase());
}

/** Every populated vibe in priority order, de-duplicated, no placeholders. */
export function getVibes(event: Partial<Event> | null | undefined): string[] {
  if (!event) return [];
  const seen = new Set<string>();
  const out: string[] = [];
  for (const raw of [event.primaryVibe, event.secondaryVibe, event.tertiaryVibe]) {
    const label = humanizeVibe(raw);
    if (!label || seen.has(label.toLowerCase())) continue;
    seen.add(label.toLowerCase());
    out.push(label);
  }
  return out;
}

// ---------------------------------------------------------------------------
// Dates and times
// ---------------------------------------------------------------------------

export interface EventDateTimeDisplay {
  /** "Fri, Aug 21, 2026" or "Aug 21 – Aug 23, 2026" for multi-day events. */
  dateLabel: string;
  /** "9:00 AM – 5:00 PM PDT", or '' when no reliable time exists. */
  timeLabel: string;
  /** Weekday of the start date, e.g. "Friday". '' when unparseable. */
  weekday: string;
  /** Zone abbreviation actually used, or null when none could be resolved. */
  timeZoneAbbreviation: string | null;
  /** ISO wall-clock value for <time dateTime>. */
  startIso: string;
  endIso: string;
  isMultiDay: boolean;
  /** "3 hours", "2 days"; '' when it cannot be computed. */
  duration: string;
}

function formatWallClockTime(value?: string | null): string {
  const parsed = parseEventLocalDateTime(value);
  if (!parsed || !hasKnownEventTime(value)) return '';
  const date = new Date(2000, 0, 15, parsed.hour, parsed.minute, parsed.second);
  return new Intl.DateTimeFormat(undefined, { hour: 'numeric', minute: '2-digit' }).format(date);
}

function formatWeekday(value?: string | null): string {
  const parsed = parseEventLocalDateTime(value);
  if (!parsed) return '';
  const date = new Date(parsed.year, parsed.month - 1, parsed.day, 12);
  return new Intl.DateTimeFormat(undefined, { weekday: 'long' }).format(date);
}

/** Build every date/time string a card or detail page needs, in one pass. */
export function getEventDateTimeDisplay(event: Partial<Event> | null | undefined): EventDateTimeDisplay {
  const empty: EventDateTimeDisplay = {
    dateLabel: '', timeLabel: '', weekday: '', timeZoneAbbreviation: null,
    startIso: '', endIso: '', isMultiDay: false, duration: '',
  };
  if (!event) return empty;

  const start = parseEventLocalDateTime(event.startDate);
  if (!start) return empty;

  const comparison = compareEventLocalDates(event.startDate, event.endDate);
  const isMultiDay = comparison != null && comparison < 0;

  const startDateText = formatEventLocalDateTime(event.startDate, { includeTime: false });
  const endDateText = formatEventLocalDateTime(event.endDate, { includeTime: false });
  const dateLabel = isMultiDay && endDateText ? `${startDateText} – ${endDateText}` : startDateText;

  const format = getEventFormat(event);
  const zone = resolveEventTimeZone(event, format, event.startDate);

  const startTime = formatWallClockTime(event.startDate);
  const endTime = isMultiDay ? '' : formatWallClockTime(event.endDate);
  let timeLabel = '';
  if (startTime) {
    timeLabel = endTime && endTime !== startTime ? `${startTime} – ${endTime}` : startTime;
    if (zone.abbreviation) timeLabel += ` ${zone.abbreviation}`;
  }

  return {
    dateLabel,
    timeLabel,
    weekday: formatWeekday(event.startDate),
    timeZoneAbbreviation: zone.abbreviation,
    startIso: event.startDate ?? '',
    endIso: event.endDate ?? '',
    isMultiDay,
    duration: formatEventLocalDuration(event.startDate, event.endDate),
  };
}

// ---------------------------------------------------------------------------
// Grouping / ordering
// ---------------------------------------------------------------------------

function todayCalendarKey(reference: Date): number {
  return reference.getFullYear() * 10000 + (reference.getMonth() + 1) * 100 + reference.getDate();
}

function calendarKeyOf(value?: string | null): number | null {
  const parsed = parseEventLocalDateTime(value);
  if (!parsed) return null;
  return parsed.year * 10000 + parsed.month * 100 + parsed.day;
}

/**
 * An event is "current" until its end time; when no end date was extracted the
 * start date stands in. Comparison is calendar-level because the event's zone
 * is frequently unknown — a same-day event is never prematurely archived.
 */
export function isUpcomingOrCurrent(event: Partial<Event>, reference: Date = new Date()): boolean {
  const effective = calendarKeyOf(event.endDate) ?? calendarKeyOf(event.startDate);
  if (effective == null) return true;
  return effective >= todayCalendarKey(reference);
}

/** Ended within the previous `RECENT_PAST_DAYS` days (14 by spec). */
export function isRecentPast(
  event: Partial<Event>,
  reference: Date = new Date(),
  windowDays: number = RECENT_PAST_DAYS,
): boolean {
  if (isUpcomingOrCurrent(event, reference)) return false;
  const effective = calendarKeyOf(event.endDate) ?? calendarKeyOf(event.startDate);
  if (effective == null) return false;
  const cutoff = new Date(reference.getFullYear(), reference.getMonth(), reference.getDate());
  cutoff.setDate(cutoff.getDate() - windowDays);
  return effective >= todayCalendarKey(cutoff);
}

export interface GroupedEvents<T extends Partial<Event>> {
  upcoming: T[];
  recentPast: T[];
}

export interface DurationGroupedEvents<T extends Partial<Event>> {
  upcoming: T[];
  ongoing: T[];
}

export function isOngoingEvent(
  event: Partial<Event>,
  durationThresholdHours: number,
  reference: Date = new Date(),
): boolean {
  const storedDuration = typeof event.durationHours === 'number' && Number.isFinite(event.durationHours)
    ? event.durationHours
    : null;
  const duration = storedDuration ?? getEventLocalDurationHours(event.startDate, event.endDate);
  if (duration == null || duration <= durationThresholdHours) return false;

  const start = calendarKeyOf(event.startDate);
  const end = calendarKeyOf(event.endDate);
  if (start == null || end == null) return false;
  const today = todayCalendarKey(reference);
  return start <= today && today <= end;
}

export function groupEventsByDuration<T extends Partial<Event>>(
  events: T[],
  durationThresholdHours: number,
  reference: Date = new Date(),
): DurationGroupedEvents<T> {
  const upcoming: T[] = [];
  const ongoing: T[] = [];

  for (const event of events || []) {
    (isOngoingEvent(event, durationThresholdHours, reference) ? ongoing : upcoming).push(event);
  }

  return { upcoming, ongoing };
}

/**
 * Split into "upcoming/current" (soonest first) and "recent past" (newest
 * first). Events older than the window are dropped entirely.
 */
export function groupEventsByRecency<T extends Partial<Event>>(
  events: T[],
  reference: Date = new Date(),
  windowDays: number = RECENT_PAST_DAYS,
): GroupedEvents<T> {
  const upcoming: T[] = [];
  const recentPast: T[] = [];

  for (const event of events || []) {
    if (isUpcomingOrCurrent(event, reference)) upcoming.push(event);
    else if (isRecentPast(event, reference, windowDays)) recentPast.push(event);
  }

  const startKey = (e: Partial<Event>) => getEventLocalSortKey(e.startDate) ?? Number.MAX_SAFE_INTEGER;
  const endKey = (e: Partial<Event>) =>
    getEventLocalSortKey(e.endDate) ?? getEventLocalSortKey(e.startDate) ?? 0;

  upcoming.sort((a, b) => startKey(a) - startKey(b));
  recentPast.sort((a, b) => endKey(b) - endKey(a));

  return { upcoming, recentPast };
}

/** Events inserted in the previous seven rolling days, ordered by event date. */
export function getRecentlyAddedEvents<T extends Partial<Event>>(
  events: T[],
  reference: Date = new Date(),
  windowDays: number = RECENTLY_ADDED_DAYS,
): T[] {
  const now = reference.getTime();
  const cutoff = now - windowDays * 24 * 60 * 60 * 1000;
  const startKey = (event: Partial<Event>) =>
    getEventLocalSortKey(event.startDate) ?? Number.MAX_SAFE_INTEGER;

  return (events || [])
    .filter((event) => {
      if (!event.dateCreated || windowDays <= 0) return false;
      const addedAt = new Date(event.dateCreated).getTime();
      return Number.isFinite(addedAt) && addedAt >= cutoff && addedAt <= now;
    })
    .sort((left, right) => startKey(left) - startKey(right));
}

// ---------------------------------------------------------------------------
// Metadata lines
// ---------------------------------------------------------------------------

export interface EventMetadataLines {
  venueName: string;
  address: string;
  locationLabel: string;
  formatLabel: string;
  eventType: string;
  audienceLevel: string;
  price: string;
  registrationDeadline: string;
  seats: string;
  speakers: string[];
  topics: string[];
  industries: string[];
}

/** Truncate on a word boundary, appending an ellipsis. */
export function truncate(text: string | null | undefined, maxLength: number): string {
  const value = (text ?? '').trim();
  if (!value || value.length <= maxLength) return value;
  const cut = value.slice(0, maxLength);
  const lastSpace = cut.lastIndexOf(' ');
  return `${(lastSpace > maxLength * 0.6 ? cut.slice(0, lastSpace) : cut).trimEnd()}…`;
}

/** Best available human label for a venue, preferring name then address. */
export function getVenueLabel(event: Partial<Event> | null | undefined): string {
  if (!event) return '';
  const structured = getVenueName(event.venue);
  if (structured) return structured;
  return typeof event.venueText === 'string' ? event.venueText.trim() : '';
}

export function getEventAddress(event: Partial<Event> | null | undefined): string {
  if (!event) return '';
  return getVenueFormattedAddress(event.venue as EventVenue | string | null | undefined);
}

export function getEventLocationLabel(event: Partial<Event> | null | undefined): string {
  if (!event) return '';
  return getLocationLabel(event);
}

/** Stable, human-facing relationship value for exports. */
export function getRelationshipText(
  event: Partial<Event> & { entities?: EventEntityRef[] },
): string {
  return getRelationshipBadges(event)
    .map((badge) => badge.label)
    .join('; ');
}

export type { EventFormat, EntityEventRelationship };
